// ─── Expense generation: delivery, orders, recurring, ad spend, modifiers ─────
import {
  MONTH_NAMES, parseDate, toDateStr, mkKey, addDays, getDaysInMonth,
  getOrderTotalCt, isMonthKey,
} from "./utils.js";
import {
  isMktActiveForKey, isProdActiveForKey,
} from "./marketplaces.js";

// ─── Multi-currency helper ────────────────────────────────────────────────
// Resolve the FX rate to apply to an order's amount, converting from the
// order's currency to the brand's base currency.
//
// Single source of truth (audit-grade, GAAP/IFRS-aligned):
//   • order.fxRate is stamped at order-creation time on the server
//     (saveBrand → getRate → Frankfurter / ECB) and is immutable.
//   • For same-currency orders the stamped rate is 1.
//   • Legacy orders (created before migration 009) have no fxRate; we
//     fall back to 1, treating the order as if entered in base currency
//     — same behaviour as the old buggy code so no math change for
//     existing data.
//
// We deliberately do NOT use the per-PT settlement rate here (calc is
// projection-oriented; settlement rates are display-only). Accounting
// tools handle "actual cash spent" via a separate reconciliation pass
// outside the projection model.
function orderFxRate(order) {
  if (!order) return 1;
  const r = Number(order.fxRate);
  return (isFinite(r) && r > 0) ? r : 1;
}

// ─── Delivery / FBM expense computation ──────────────────────────────────────
// IMPORTANT: A product's own `deliveryCost` field is NEVER turned into a Cash
// Calendar expense row. Instead it's optionally subtracted from payout via
// `deductDeliveryFromPayout` (handled in marketplaces.js > effectivePayoutForMkt).
// Only marketplace-level / FBM-settings delivery (a separate fulfillment provider
// the user has set up) generates expense rows here.
function getEffectiveDeliveryCost(prod, mkt, fbmSettings = []) {
  for (const fbm of fbmSettings) {
    if (fbm.costType !== "delivery") continue;
    if (!fbm.costPerUnit || fbm.costPerUnit <= 0) continue;
    const scope = fbm.marketplaceScope || "all";
    const covers = scope === "all" || (Array.isArray(fbm.marketplaceIds) && fbm.marketplaceIds.includes(mkt.id));
    if (covers) return fbm.costPerUnit;
  }
  return 0;
}

export function computeMonthlyCost(target, marketplaces, mv, fbmSettings = [], excludeFixed = false) {
  let total = 0;
  for (const mkt of marketplaces) {
    const mktPct = mv[`pct_${mkt.id}`] ?? mkt.pctOfTotal;
    for (const p of mkt.products) {
      const pPct = mv[`pct_${mkt.id}_${p.id}`] ?? p.pctOfMarket;
      const del = mv[`deliv_${mkt.id}_${p.id}`] ?? getEffectiveDeliveryCost(p, mkt, fbmSettings);
      total += target * mktPct * pPct * del;
    }
  }
  if (!excludeFixed) {
    for (const fbm of fbmSettings) {
      if (fbm.costType === "flat" && fbm.flatMonthly > 0) total += fbm.flatMonthly;
      if (fbm.costType === "storage") {
        const sc = fbm.storageCalc || "per_unit";
        if (sc === "flat") total += fbm.flatMonthly || 0;
        else if (sc === "std_pallet") total += (fbm.stdPallets || 0) * (fbm.stdPalletCost || 0);
        else if (sc === "os_pallet") total += (fbm.osPallets || 0) * (fbm.osPalletCost || 0);
        else total += target * (fbm.costPerUnit || 0);
      }
    }
  }
  return total;
}

export function computeDeliveryExpenses(months, marketplaces, limit, fbmSettings = []) {
  try {
  if (!months || !Array.isArray(marketplaces)) return {};
  const activeFbm = fbmSettings.filter(f => f.costType !== "flat" && f.costType !== "storage");
  const primaryFbm = activeFbm[0];
  const triggerType = primaryFbm?.triggerType || "credit_limit";
  const creditLim = (triggerType === "credit_limit" || triggerType === "both")
    ? (primaryFbm?.creditLimit || limit || 0)
    : (limit ?? 0);
  const periodDays = primaryFbm?.creditPeriodDays || 30;

  const limStart = primaryFbm?.creditLimitStart ? parseDate(primaryFbm.creditLimitStart) : null;
  const perStart = primaryFbm?.creditPeriodStart ? parseDate(primaryFbm.creditPeriodStart) : null;
  const fixStart = primaryFbm?.fixedDayStart ? parseDate(primaryFbm.fixedDayStart) : null;
  const fbmName = primaryFbm?.name || "Fulfilment Cost";

  const result = {};
  let expIdx = 0;
  const allKeys = Object.keys(months).filter(isMonthKey).sort();

  // Helper: filter FBM settings by activeMonths for a given calendar month
  const fbmForMonth = (m) => fbmSettings.filter(f => {
    const am = f.activeMonths;
    if (!am || !Array.isArray(am) || am.length === 0 || am.length === 12) return true;
    return am.includes(m);
  });

  if (triggerType === "fixed_day") {
    for (const key of allKeys) {
      const mData = months[key] || { target: 0 };
      const mv = mData.vars || {};
      const [year, m] = key.split("-").map(Number);
      if (fixStart && new Date(year, m-1, 1) < fixStart) continue;
      const cost = computeMonthlyCost(mData.target || 0, marketplaces, mv, fbmForMonth(m), true);
      if (cost <= 0) continue;
      const dim = getDaysInMonth(year, m);
      const day = Math.min(primaryFbm?.fixedDay || 1, dim);
      result[key] = [{ id: `delivery_${++expIdx}`, amount: Math.round(cost), what: `📦 ${fbmName} — ${MONTH_NAMES[m-1]} ${year}`, date: toDateStr(year, m, day) }];
    }
    return result;
  }

  const dailyCosts = [];
  for (const key of allKeys) {
    const mData = months[key] || { target: 0 };
    const mv = mData.vars || {};
    const [year, m] = key.split("-").map(Number);
    const cost = computeMonthlyCost(mData.target || 0, marketplaces, mv, fbmForMonth(m), true);
    const dim = getDaysInMonth(year, m);
    const daily = cost / dim;
    for (let d = 1; d <= dim; d++) {
      dailyCosts.push({ year, month: m, day: d, cost: daily, key });
    }
  }

  if (triggerType === "credit_period" || triggerType === "both") {
    let periodStartIdx = 0;
    if (perStart) {
      periodStartIdx = dailyCosts.findIndex(dc =>
        new Date(dc.year, dc.month-1, dc.day) >= perStart
      );
      if (periodStartIdx < 0) periodStartIdx = 0;
    }

    let accumulated = 0;
    let daysSinceBilling = 0;
    let cumulativeForLimit = 0;
    let limitThreshold = creditLim > 0 ? creditLim : Infinity;

    for (let i = 0; i < dailyCosts.length; i++) {
      const dc = dailyCosts[i];
      if (dc.cost <= 0) continue;

      const dcDate = new Date(dc.year, dc.month-1, dc.day);
      if (perStart && dcDate < perStart) continue;
      if (limStart && dcDate < limStart && triggerType === "both") continue;

      accumulated += dc.cost;
      cumulativeForLimit += dc.cost;
      daysSinceBilling++;

      let shouldBill = false;
      let billReason = "";

      if (daysSinceBilling >= periodDays) {
        shouldBill = true;
        billReason = `${periodDays}-day cycle`;
      }

      if (triggerType === "both" && creditLim > 0 && cumulativeForLimit >= limitThreshold) {
        shouldBill = true;
        billReason = "credit limit reached";
        limitThreshold += creditLim;
      }

      if (shouldBill && accumulated > 0) {
        const key = mkKey(dc.year, dc.month);
        if (!result[key]) result[key] = [];
        result[key].push({
          id: `delivery_${++expIdx}`,
          amount: Math.round(accumulated),
          what: `📦 ${fbmName} — ${billReason}`,
          date: toDateStr(dc.year, dc.month, dc.day),
        });
        accumulated = 0;
        daysSinceBilling = 0;
      }
    }
    return result;
  }

  if (creditLim <= 0) {
    for (const key of allKeys) {
      const mData = months[key] || { target: 0 };
      const mv = mData.vars || {};
      const [year, m] = key.split("-").map(Number);
      const cost = computeMonthlyCost(mData.target || 0, marketplaces, mv, fbmForMonth(m), true);
      if (cost <= 0) continue;
      const dim = getDaysInMonth(year, m);
      const daily = cost / dim;
      const exps = [];
      for (let d = 1; d <= dim; d++) {
        exps.push({ id: `delivery_${++expIdx}`, amount: Math.round(daily), what: `📦 ${fbmName} — ${MONTH_NAMES[m-1]} ${year} (daily)`, date: toDateStr(year, m, d) });
      }
      result[key] = exps;
    }
    return result;
  }

  let cumulative = 0;
  for (const key of allKeys) {
    const mData = months[key] || { target: 0 };
    const mv = mData.vars || {};
    const [year, m] = key.split("-").map(Number);
    const monthEnd = new Date(year, m, 0);
    if (limStart && monthEnd < limStart) continue;
    let cost = computeMonthlyCost(mData.target || 0, marketplaces, mv, fbmForMonth(m), true);
    if (cost <= 0) continue;
    const dim = getDaysInMonth(year, m);
    if (limStart && new Date(year, m-1, 1) < limStart && limStart.getMonth()+1 === m && limStart.getFullYear() === year) {
      const daysActive = dim - limStart.getDate() + 1;
      cost = cost * (daysActive / dim);
    }
    const prev = cumulative;
    cumulative += cost;
    const monthExp = [];
    let threshold = Math.ceil((prev + 0.01) / creditLim) * creditLim;
    while (threshold <= cumulative) {
      const frac = cost > 0 ? (threshold - prev) / cost : 0;
      const day = Math.min(dim, Math.max(1, Math.ceil(frac * dim)));
      monthExp.push({ id: `delivery_${++expIdx}`, amount: creditLim, what: `📦 ${fbmName} — credit limit reached`, date: toDateStr(year, m, day) });
      threshold += creditLim;
    }
    if (monthExp.length) result[key] = monthExp;
  }

  const storageFbm = fbmSettings.filter(f => f.costType === "storage" || f.costType === "flat");
  if (storageFbm.length > 0) {
    for (const key of allKeys) {
      const [year, m] = key.split("-").map(Number);
      const mData = months[key] || {};
      const target = mData.target || 0;
      for (const fbm of storageFbm) {
        let cost = 0, label = "";
        if (fbm.costType === "flat") {
          cost = fbm.flatMonthly || 0;
          label = `📋 ${fbm.name || "Flat Monthly Fee"} — ${MONTH_NAMES[m-1]} ${year}`;
        } else if (fbm.costType === "storage") {
          const sc = fbm.storageCalc || "per_unit";
          if (sc === "flat") { cost = fbm.flatMonthly || 0; label = `🏗 ${fbm.name || "Storage"} — flat fee ${MONTH_NAMES[m-1]} ${year}`; }
          else if (sc === "std_pallet") { cost = (fbm.stdPallets||0)*(fbm.stdPalletCost||0); label = `🏗 ${fbm.name || "Storage"} — ${fbm.stdPallets||0} std pallets ${MONTH_NAMES[m-1]} ${year}`; }
          else if (sc === "os_pallet") { cost = (fbm.osPallets||0)*(fbm.osPalletCost||0); label = `🏗 ${fbm.name || "Storage"} — ${fbm.osPallets||0} OS pallets ${MONTH_NAMES[m-1]} ${year}`; }
          else { cost = target * (fbm.costPerUnit||0); label = `🏗 ${fbm.name || "Storage"} — ${target} units ${MONTH_NAMES[m-1]} ${year}`; }
          if ((fbm.stdPallets||0) > 0 && (fbm.osPallets||0) > 0 && sc !== "flat" && sc !== "per_unit") {
            cost = (fbm.stdPallets||0)*(fbm.stdPalletCost||0) + (fbm.osPallets||0)*(fbm.osPalletCost||0);
            label = `🏗 ${fbm.name || "Storage"} — ${fbm.stdPallets} std + ${fbm.osPallets} OS pallets ${MONTH_NAMES[m-1]} ${year}`;
          }
        }
        if (cost <= 0) continue;
        if (!result[key]) result[key] = [];
        result[key].push({ id: `storage_${fbm.id}_${key}`, amount: Math.round(cost), what: label, date: toDateStr(year, m, 1) });
      }
    }
  }

  return result;
  } catch (err) { console.warn("[computeDeliveryExpenses] caught:", err?.message || err); return {}; }
}

// ─── Order expense generation ─────────────────────────────────────────────────
export function generateOrderExpenses(orders) {
  try {
  if (!Array.isArray(orders)) return {};
  const result = {};

  for (const order of orders) {
    if (!order) continue;
    const val = Number(order.amount) || 0;
    // Multi-currency: convert to brand base using the rate stamped at
    // order creation. See orderFxRate() above for the design rationale.
    const rate0 = orderFxRate(order);
    const baseAmt = val * rate0;
    const totalCt = getOrderTotalCt(order);

    const pt0 = order.paymentTerms?.[0];
    if (pt0 && baseAmt > 0) {
      const pt0DateStr = pt0.date || order.orderDate;
      const d = pt0DateStr ? parseDate(pt0DateStr) : null;
      if (d) {
        const key = mkKey(d.getFullYear(), d.getMonth() + 1);
        if (!result[key]) result[key] = [];
        result[key].push({
          id: `ord_${order.id}_pt0`,
          amount: baseAmt * (pt0.pct / 100),
          what: `💳 ${order.number} — ${pt0.label || "1st Payment"} (${pt0.pct}%)`,
          date: toDateStr(d.getFullYear(), d.getMonth() + 1, d.getDate()),
          orderId: order.id, ptIdx: 0, batchId: null, marketplaceIds: order.marketplaceIds || [],
        });
      }
    }

    const shipCostPerCt = order.shippingCostPerContainer || 0;
    if (shipCostPerCt > 0) {
      for (const cb of (order.containerBatches || [])) {
        const arrival = parseDate(cb.arrivalDate);
        if (!arrival) continue;
        const payDate = addDays(arrival, order.shippingPaymentDays || 0);
        const shipAmt = (cb.shippingCostOverride != null ? cb.shippingCostOverride : shipCostPerCt) * (cb.containers || 0);
        if (shipAmt <= 0) continue;
        const key = mkKey(payDate.getFullYear(), payDate.getMonth() + 1);
        if (!result[key]) result[key] = [];
        result[key].push({
          id: `ord_${order.id}_ship_cb${cb.id}`,
          amount: shipAmt,
          what: `🚢 ${order.number} — Shipping Batch ${(order.containerBatches || []).indexOf(cb) + 1} (${cb.containers} CT)`,
          date: toDateStr(payDate.getFullYear(), payDate.getMonth() + 1, payDate.getDate()),
          orderId: order.id, ptIdx: "ship", batchId: cb.id, marketplaceIds: order.marketplaceIds || [],
        });
      }
    }

    for (const cb of (order.containerBatches || [])) {
      if (!cb.insuranceCost || cb.insuranceCost <= 0) continue;
      const departure = parseDate(cb.departureDate);
      if (!departure) continue;
      const key = mkKey(departure.getFullYear(), departure.getMonth() + 1);
      if (!result[key]) result[key] = [];
      result[key].push({
        id: `ord_${order.id}_insurance_${cb.id}`,
        amount: cb.insuranceCost,
        what: `🛡 ${order.number} — Insurance Batch ${(order.containerBatches || []).indexOf(cb) + 1}`,
        date: toDateStr(departure.getFullYear(), departure.getMonth() + 1, departure.getDate()),
        orderId: order.id, ptIdx: "insurance", batchId: cb.id, marketplaceIds: order.marketplaceIds || [],
      });
    }

    // Re-use the same stamped rate for duty calculations (different scope
    // in this function but conceptually the same conversion).
    const rate = orderFxRate(order);
    const baseVal = val * rate;

    if (order.incoterms === "ddu" && (order.dutyPct > 0 || order.dutyFlat > 0)) {
      for (const cb of (order.containerBatches || [])) {
        const arrival = parseDate(cb.arrivalDate);
        if (!arrival) continue;
        const dutyDays = order.dutyDaysAfter || 0;
        const dutyDate = addDays(arrival, dutyDays);
        const batchFraction = totalCt > 0 ? (cb.containers || 0) / totalCt : 1;
        const dutyAmt = order.dutyFlat > 0
          ? order.dutyFlat * batchFraction
          : baseVal * ((order.dutyPct || 0) / 100) * batchFraction;
        if (dutyAmt <= 0) continue;
        const key = mkKey(dutyDate.getFullYear(), dutyDate.getMonth() + 1);
        if (!result[key]) result[key] = [];
        result[key].push({
          id: `ord_${order.id}_duty_cb${cb.id}`,
          amount: dutyAmt,
          what: `🛃 ${order.number} — Import Duty Batch ${(order.containerBatches || []).indexOf(cb) + 1} (${cb.containers || 0} CT)${order.dutyPct > 0 ? ` ${order.dutyPct}%` : ""}`,
          date: toDateStr(dutyDate.getFullYear(), dutyDate.getMonth() + 1, dutyDate.getDate()),
          orderId: order.id, ptIdx: "duty", batchId: cb.id, marketplaceIds: order.marketplaceIds || [],
        });
      }
    }

    if (order.inspectionCost > 0 && order.inspectionDate) {
      const inspD = parseDate(order.inspectionDate);
      if (inspD) {
        const key = mkKey(inspD.getFullYear(), inspD.getMonth() + 1);
        if (!result[key]) result[key] = [];
        result[key].push({
          id: `ord_${order.id}_inspection`,
          amount: order.inspectionCost * rate,
          what: `🔍 ${order.number} — Quality Inspection`,
          date: toDateStr(inspD.getFullYear(), inspD.getMonth() + 1, inspD.getDate()),
          orderId: order.id, ptIdx: "inspection", batchId: null, marketplaceIds: order.marketplaceIds || [],
        });
      }
    }

    for (const cb of (order.containerBatches || [])) {
      const arrival = parseDate(cb.arrivalDate);
      const departure = parseDate(cb.departureDate);
      if (!arrival) continue;
      const ptDates = {};
      for (let ptIdx = 1; ptIdx < (order.paymentTerms || []).length; ptIdx++) {
        const pt = order.paymentTerms[ptIdx];
        if (!pt || pt.triggerType === "manual") continue;
        let payDate;
        const days = pt.daysAfter || 0;
        if (pt.triggerType === "days_after_order") {
          const orderD = parseDate(order.orderDate);
          if (orderD) payDate = addDays(orderD, days);
        } else if (pt.triggerType === "days_after_departure") {
          if (departure) payDate = addDays(departure, days);
        } else if (pt.triggerType === "days_after_arrival") {
          payDate = addDays(arrival, days);
        } else if (pt.triggerType === "days_after_previous") {
          let prevDate = arrival;
          for (let prev = ptIdx - 1; prev >= 1; prev--) {
            if (ptDates[prev]) { prevDate = ptDates[prev]; break; }
          }
          payDate = addDays(prevDate, days);
        }
        if (payDate) ptDates[ptIdx] = payDate;
      }

      for (let ptIdx = 1; ptIdx < (order.paymentTerms || []).length; ptIdx++) {
        const pt = order.paymentTerms[ptIdx];
        if (!pt || pt.triggerType === "manual") continue;
        // Every per-batch payment term fires for every batch (see getOrderCashEvents)
        const payDate = ptDates[ptIdx];
        if (!payDate) continue;

        let batchAmt;
        if (cb.customAmounts?.[ptIdx] != null) {
          batchAmt = cb.customAmounts[ptIdx];
        } else {
          batchAmt = (baseAmt * (pt.pct / 100) / totalCt) * (cb.containers || 0);
        }
        if (batchAmt <= 0) continue;

        const key = mkKey(payDate.getFullYear(), payDate.getMonth() + 1);
        if (!result[key]) result[key] = [];
        result[key].push({
          id: `ord_${order.id}_pt${ptIdx}_cb${cb.id}`,
          amount: batchAmt,
          what: `💳 ${order.number} — ${pt.label || `Payment ${ptIdx + 1}`} Batch ${(order.containerBatches || []).indexOf(cb) + 1} (${pt.pct}%, ${cb.containers} CT)`,
          date: toDateStr(payDate.getFullYear(), payDate.getMonth() + 1, payDate.getDate()),
          orderId: order.id, ptIdx, batchId: cb.id, marketplaceIds: order.marketplaceIds || [],
        });
      }
    }
  }
  return result;
  } catch (err) { console.warn("[generateOrderExpenses] caught:", err?.message || err); return {}; }
}

// ─── Individual order cash events for chart visualization ──────────────────
export function getOrderCashEvents(order) {
  const events = [];
  const warnings = [];

  if (!order) return { events, warnings };

  const val = order.amount || 0;
  // Multi-currency: see orderFxRate() at the top of this file
  const rate = orderFxRate(order);
  const baseAmt = val * rate;
  const totalCt = getOrderTotalCt(order);
  const orderDate = parseDate(order.orderDate);
  const isComplete = !!order.completed;

  if (val <= 0 && (order.paymentTerms || []).length > 0) {
    warnings.push({ message: `Order amount is $0 — payment terms exist but nothing to pay` });
  }

  if (!orderDate && !(order.paymentTerms || []).some(pt => pt.date)) {
    if (val > 0) warnings.push({ message: `No dates set — cannot plot ${val.toLocaleString()} in payments on timeline` });
    return { events, warnings };
  }

  const pt0 = order.paymentTerms?.[0];
  if (pt0 && baseAmt > 0) {
    const pt0DateStr = pt0.date || order.orderDate;
    const d = pt0DateStr ? parseDate(pt0DateStr) : null;
    if (d) {
      events.push({
        date: d, amount: baseAmt * (pt0.pct / 100),
        label: `${pt0.label || "1st payment"} (${pt0.pct}%)`,
        type: "payment_term", ptIdx: 0, batchId: null, paid: isComplete,
      });
    } else {
      warnings.push({ message: `${pt0.label || "1st payment"}: ${(baseAmt * pt0.pct / 100).toLocaleString()} — no date set`, ptIdx: 0, amount: baseAmt * pt0.pct / 100 });
    }
  }

  if ((order.paymentTerms || []).length === 0 && baseAmt > 0) {
    if (orderDate) {
      events.push({
        date: orderDate, amount: baseAmt,
        label: `Full payment (no terms defined)`,
        type: "payment_term", ptIdx: -1, batchId: null, paid: isComplete,
      });
      warnings.push({ message: `No payment terms — full amount shown on order date` });
    }
  }

  const shipCostPerCt = order.shippingCostPerContainer || 0;
  for (const cb of (order.containerBatches || [])) {
    const shipAmt = (cb.shippingCostOverride != null ? cb.shippingCostOverride : shipCostPerCt) * (cb.containers || 0);
    if (shipAmt <= 0) continue;

    const departure = parseDate(cb.departureDate);
    const arrival = parseDate(cb.arrivalDate);
    const shippingDays = order.shippingPaymentDays || 0;
    const shippingTrigger = order.shippingPaymentTrigger || "arrival";

    let payDate = null;
    let missingRef = null;

    if (shippingTrigger === "departure" || (!order.shippingPaymentTrigger && !arrival)) {
      if (departure) payDate = addDays(departure, shippingDays);
      else missingRef = "departure date";
    } else if (shippingTrigger === "fixed" && cb.shippingPayDate) {
      payDate = parseDate(cb.shippingPayDate);
      if (!payDate) missingRef = "shipping payment date";
    } else {
      if (arrival) payDate = addDays(arrival, shippingDays);
      else missingRef = "arrival date";
    }

    if (payDate) {
      events.push({
        date: payDate, amount: shipAmt,
        label: `Shipping Batch ${(order.containerBatches || []).indexOf(cb) + 1} (${cb.containers || 0} CT)`,
        type: "shipping", ptIdx: "ship", batchId: cb.id, paid: isComplete,
      });
    } else {
      warnings.push({
        message: `Batch ${(order.containerBatches || []).indexOf(cb) + 1} freight: ${shipAmt.toLocaleString()} — ${missingRef} missing`,
        batchId: cb.id, amount: shipAmt,
      });
    }
  }

  if (order.incoterms === "ddu" && (order.dutyPct > 0 || order.dutyFlat > 0)) {
    for (const cb of (order.containerBatches || [])) {
      const arrival = parseDate(cb.arrivalDate);
      const batchFraction = totalCt > 0 ? (cb.containers || 0) / totalCt : 1;
      const dutyAmt = order.dutyFlat > 0
        ? order.dutyFlat * batchFraction
        : baseAmt * ((order.dutyPct || 0) / 100) * batchFraction;
      if (dutyAmt <= 0) continue;

      if (arrival) {
        const dutyDate = addDays(arrival, order.dutyDaysAfter || 0);
        events.push({
          date: dutyDate, amount: dutyAmt,
          label: `Customs/Duty Batch ${(order.containerBatches || []).indexOf(cb) + 1}`,
          type: "customs", ptIdx: "duty", batchId: cb.id, paid: isComplete,
        });
      } else {
        warnings.push({
          message: `Batch ${(order.containerBatches || []).indexOf(cb) + 1} customs: ${dutyAmt.toLocaleString()} — arrival date missing`,
          batchId: cb.id, amount: dutyAmt,
        });
      }
    }
  }

  if (order.inspectionCost > 0) {
    const inspD = parseDate(order.inspectionDate);
    if (inspD) {
      events.push({
        date: inspD, amount: order.inspectionCost * rate,
        label: `Quality Inspection`,
        type: "inspection", ptIdx: "inspection", batchId: null, paid: isComplete,
      });
    } else {
      warnings.push({ message: `Inspection cost: ${(order.inspectionCost * rate).toLocaleString()} — inspection date not set`, amount: order.inspectionCost * rate });
    }
  }

  for (const cb of (order.containerBatches || [])) {
    const arrival = parseDate(cb.arrivalDate);
    const departure = parseDate(cb.departureDate);
    const batchNum = (order.containerBatches || []).indexOf(cb) + 1;

    const ptDates = {};
    for (let ptIdx = 1; ptIdx < (order.paymentTerms || []).length; ptIdx++) {
      const pt = order.paymentTerms[ptIdx];
      if (!pt || pt.triggerType === "manual") continue;
      let payDate = null;
      let missingRef = null;
      const days = pt.daysAfter || 0;

      if (pt.triggerType === "days_after_order") {
        if (orderDate) payDate = addDays(orderDate, days);
        else missingRef = "order date";
      } else if (pt.triggerType === "days_after_departure") {
        if (departure) payDate = addDays(departure, days);
        else missingRef = "departure date";
      } else if (pt.triggerType === "days_after_arrival") {
        if (arrival) payDate = addDays(arrival, days);
        else missingRef = "arrival date";
      } else if (pt.triggerType === "days_after_previous") {
        let prevDate = arrival;
        for (let prev = ptIdx - 1; prev >= 1; prev--) {
          if (ptDates[prev]) { prevDate = ptDates[prev]; break; }
        }
        if (prevDate) payDate = addDays(prevDate, days);
        else missingRef = "previous payment date";
      }

      if (payDate) ptDates[ptIdx] = payDate;
    }

    for (let ptIdx = 1; ptIdx < (order.paymentTerms || []).length; ptIdx++) {
      const pt = order.paymentTerms[ptIdx];
      if (!pt || pt.triggerType === "manual") continue;
      // Every per-batch payment term fires for every batch. Each term's total %
      // is already pro-rated by container count (line below), so N terms × M batches
      // generates N×M diamonds on the chart. Previously we restricted to the
      // batch's "linkedPtIdx" which hid later payment terms (e.g. "50% 60d after arrival").
      const payDate = ptDates[ptIdx];

      let batchAmt;
      if (cb.customAmounts?.[ptIdx] != null) {
        batchAmt = cb.customAmounts[ptIdx];
      } else {
        batchAmt = totalCt > 0 ? (baseAmt * (pt.pct / 100) / totalCt) * (cb.containers || 0) : 0;
      }
      if (batchAmt <= 0) continue;

      if (payDate) {
        events.push({
          date: payDate, amount: batchAmt,
          label: `${pt.label || `Payment ${ptIdx + 1}`} Batch ${batchNum} (${cb.containers || 0} CT)`,
          type: "payment_term", ptIdx, batchId: cb.id, paid: isComplete,
        });
      } else {
        if (orderDate) {
          events.push({
            date: orderDate, amount: batchAmt,
            label: `${pt.label || `Payment ${ptIdx + 1}`} Batch ${batchNum} (${cb.containers || 0} CT)`,
            type: "payment_term", ptIdx, batchId: cb.id, paid: isComplete,
          });
          const triggerLabel = (pt.triggerType || "").replace(/_/g, " ");
          warnings.push({ message: `Batch ${batchNum} ${pt.label || `PT${ptIdx+1}`}: ${batchAmt.toLocaleString()} shown on order date — ${triggerLabel} date missing`, batchId: cb.id, ptIdx, amount: batchAmt });
        } else {
          warnings.push({ message: `Batch ${batchNum} ${pt.label || `PT${ptIdx+1}`}: ${batchAmt.toLocaleString()} cannot be plotted — no reference date available`, batchId: cb.id, ptIdx, amount: batchAmt });
        }
      }
    }
  }

  events.sort((a, b) => a.date.getTime() - b.date.getTime());

  return { events, warnings };
}

// ─── Planner date parser ──────────────────────────────────────────────────────
export function parsePlannerDate(d) {
  if (!d) return null;
  if (d.prec === "day" && d.day) {
    const parts = d.day.split(".");
    if (parts.length === 3) return new Date(+parts[2], +parts[1]-1, +parts[0]);
  }
  if (d.prec === "month" && d.month) {
    const [y,m] = d.month.split("-").map(Number);
    return new Date(y, m-1, 1);
  }
  if (d.prec === "quarter" && d.quarter) {
    const [y,q] = d.quarter.split("-Q").map(Number);
    return new Date(y, (q-1)*3, 1);
  }
  if (typeof d === "object" && d.date) return parsePlannerDate(d.date);
  return null;
}

// ─── Decision-based recurring cashflow generation ────────────────────────────
export function generateDecisionRecurringItems(decisions, visibleYears) {
  const result = {};
  if (!decisions || !visibleYears) return result;

  for (const d of decisions) {
    if (!d.recurring || !d.recurringInterval) continue;
    const startDate = parsePlannerDate(d);
    if (!startDate) continue;

    const amount = Math.abs(d.recurringAmount || 0);
    if (amount <= 0) continue;
    const isIncome = d.recurringIsIncome === true;

    const intervalMonths = d.recurringInterval === "monthly" ? 1
      : d.recurringInterval === "quarterly" ? 3 : 12;

    for (const year of visibleYears) {
      for (let m = 1; m <= 12; m++) {
        const monthsFromStart =
          (year - startDate.getFullYear()) * 12 + (m - (startDate.getMonth() + 1));
        if (monthsFromStart < 0) continue;
        if (monthsFromStart % intervalMonths !== 0) continue;

        const key = mkKey(year, m);
        if (!result[key]) result[key] = [];
        result[key].push({
          id: "drec_" + d.id + "_" + year + "_" + m,
          amount,
          what: (d.icon || "📌") + " " + (d.name || "Decision") + " (recurring)",
          date: toDateStr(year, m, startDate.getDate() || 1),
          isIncome,
          decisionId: d.id,
          recurringId: d.id,
        });
      }
    }
  }
  return result;
}

// ─── One-time decision impacts → cashflow entries ────────────────────────────
export function generateDecisionOneTimeItems(decisions, visibleYears) {
  const result = {};
  if (!decisions || !visibleYears) return result;

  for (const d of decisions) {
    if (!d.impact) continue;
    const impType = d.impact.type || "";
    if (impType !== "one_time_expense" && impType !== "one_time_income") continue;

    const amount = Math.abs(Number(d.impact.amount) || 0);
    if (amount <= 0) continue;

    const startDate = parsePlannerDate(d);
    if (!startDate) continue;

    const year = startDate.getFullYear();
    const m = startDate.getMonth() + 1;
    if (!visibleYears.includes(year)) continue;

    const isIncome = impType === "one_time_income";
    const key = mkKey(year, m);
    if (!result[key]) result[key] = [];
    result[key].push({
      id: "donce_" + d.id,
      amount,
      what: (d.icon || "📌") + " " + (d.name || "Decision") + " (one-time " + (isIncome ? "income" : "cost") + ")",
      date: toDateStr(year, m, startDate.getDate() || 1),
      isIncome,
      decisionId: d.id,
      recurringId: d.id,
    });
  }
  return result;
}

// ─── Recurring expense/income generation ─────────────────────────────────────
export function generateRecurringItems(items, visibleYears) {
  try {
  if (!Array.isArray(items)) return {};
  const result = {};

  for (const item of items) {
    const startDate = parseDate(item.startDate);

    if (item.type === "simple" && !startDate) continue;

    for (const year of visibleYears) {
      for (let m = 1; m <= 12; m++) {
        const key = mkKey(year, m);
        const dim = getDaysInMonth(year, m);

        if (item.type === "simple") {
          const interval = item.intervalDays || 30;
          const targetStart = new Date(year, m - 1, 1);
          let cur = new Date(startDate);
          if (cur < targetStart && interval > 0) {
            const msToTarget = targetStart.getTime() - cur.getTime();
            const stepsNeeded = Math.floor(msToTarget / (interval * 86400000));
            cur = addDays(cur, stepsNeeded * interval);
            while (cur.getFullYear() < year || (cur.getFullYear() === year && cur.getMonth() + 1 < m)) {
              cur = addDays(cur, interval);
            }
          }
          while (cur.getFullYear() === year && cur.getMonth() + 1 === m) {
            if (!result[key]) result[key] = [];
            if (item.endDate) {
              const ed = parseDate(item.endDate);
              if (ed && cur > ed) break;
            }
            const simpleGr = parseFloat(item.growthRate) || 0;
            const simpleGe = parseInt(item.growthEvery) || 1;
            const msFromStart = startDate ? (cur.getTime() - startDate.getTime()) : 0;
            const simpleTxIdx = startDate && item.intervalDays ? Math.round(msFromStart / ((item.intervalDays||30) * 86400000)) : 0;
            const simpleMult  = simpleGr > 0 && simpleTxIdx >= 0 ? Math.min(10, Math.pow(1 + simpleGr/100, Math.floor(simpleTxIdx / simpleGe))) : 1; // cap at 10x
            result[key].push({
              id: `rec_${item.id}_${cur.getTime()}`,
              amount: Math.round(((item.amount || 0) * simpleMult) * 100) / 100,
              what: (item.isIncome ? "💰 " : "🔄 ") + (item.name || "Recurring"),
              date: toDateStr(cur.getFullYear(), cur.getMonth() + 1, cur.getDate()),
              recurringId: item.id,
              isIncome: item.isIncome === true,
            });
            cur = addDays(cur, item.intervalDays || 30);
          }
        } else if (!item.type || item.type === "calendar") {
          const activeMonths = (item.activeMonths && item.activeMonths.length > 0)
            ? item.activeMonths
            : [1,2,3,4,5,6,7,8,9,10,11,12];
          const activeYears = (item.activeYears && item.activeYears.length > 0)
            ? item.activeYears
            : visibleYears;
          if (!activeMonths.includes(m) || !activeYears.includes(year)) continue;
          if (startDate) {
            if (year < startDate.getFullYear() || (year === startDate.getFullYear() && m < startDate.getMonth() + 1)) continue;
          }
          if (item.endDate) {
            const ed = parseDate(item.endDate);
            if (ed && (year > ed.getFullYear() || (year === ed.getFullYear() && m > ed.getMonth() + 1))) continue;
          }
          const startYear  = startDate ? startDate.getFullYear() : (activeYears[0] || year);
          const startMonth = startDate ? startDate.getMonth() + 1 : (activeMonths[0] || 1);
          const txIndex    = (year - startYear) * 12 + (m - startMonth);
          const gr         = parseFloat(item.growthRate) || 0;
          const ge         = parseInt(item.growthEvery) || 1;
          const growthMult = gr > 0 && txIndex >= 0 ? Math.min(10, Math.pow(1 + gr / 100, Math.floor(txIndex / ge))) : 1; // cap at 10x
          const day = Math.min(dim, item.dayOfMonth || 1);
          if (!result[key]) result[key] = [];
          result[key].push({
            id: `rec_${item.id}_${key}`,
            amount: Math.round(((item.amount || 0) * growthMult) * 100) / 100,
            what: (item.isIncome ? "💰 " : "🔄 ") + (item.name || "Recurring"),
            date: toDateStr(year, m, day),
            recurringId: item.id,
            isIncome: item.isIncome === true,
          });
        }
      }
    }
  }
  // VAT reclaim
  for (const item of (items || [])) {
    if (item.category !== "vat") continue;
    if (!item.reclaimPeriod || item.reclaimPeriod === "never") continue;
    if (item.scheme === "non-registered") continue;
    const offsetMonths = item.reclaimPeriod === "monthly" ? 1 : item.reclaimPeriod === "quarterly" ? 3 : 12;
    for (const [key, entries] of Object.entries(result)) {
      for (const entry of entries) {
        if (entry.recurringId !== item.id || entry.isIncome) continue;
        const [yr, mo] = key.split("-").map(Number);
        let reclaimM = mo + offsetMonths, reclaimY = yr;
        reclaimY += Math.floor((reclaimM - 1) / 12);
        reclaimM = ((reclaimM - 1) % 12) + 1;
        const reclaimKey = mkKey(reclaimY, reclaimM);
        if (!result[reclaimKey]) result[reclaimKey] = [];
        const dim = getDaysInMonth(reclaimY, reclaimM);
        result[reclaimKey].push({
          id: `${entry.id}_vatreclaim`,
          amount: entry.amount,
          what: `💰 VAT Reclaim — ${item.name || "VAT"}`,
          date: toDateStr(reclaimY, reclaimM, Math.min(entry.date ? parseInt(entry.date.split(/[.\-/]/)[0]) || 15 : 15, dim)),
          recurringId: `${item.id}_vatreclaim`,
          isIncome: true,
        });
      }
    }
  }

  return result;
  } catch (err) { console.warn("[generateRecurringItems] caught:", err?.message || err); return {}; }
}

// Deterministic sort key for modifiers — guarantees that `applyModifiers`
// and `applyExpenseModifiers` return the same result regardless of the
// order the caller passed the array in. All current modifier operations
// are multiplicative (`x * (1 + pct)`) and therefore commutative under
// real-number math, but float rounding differences are bounded and
// sorting prevents a future non-commutative modifier type from
// silently introducing non-determinism.
function sortedModifiers(modifiers) {
  if (!Array.isArray(modifiers) || modifiers.length < 2) return modifiers || [];
  return [...modifiers].sort((a, b) =>
    ((a?.order ?? 0) - (b?.order ?? 0)) ||
    String(a?.createdAt || "").localeCompare(String(b?.createdAt || "")) ||
    String(a?.id || "").localeCompare(String(b?.id || ""))
  );
}

// ─── Apply global modifiers to marketplaces for a month key ──────────────────
export function applyModifiers(marketplaces, modifiers, key) {
  if (!modifiers || modifiers.length === 0) return marketplaces;
  const sortedMods = sortedModifiers(modifiers);

  return marketplaces.map(mkt => {
    let mktPctOfTotal = mkt.pctOfTotal;
    let products = mkt.products.map(p => ({ ...p }));

    for (const mod of sortedMods) {
      if (mod.type !== "payout" && mod.type !== "units") continue;

      const activeKeys = Array.isArray(mod.monthKeys) && mod.monthKeys.length > 0
        ? mod.monthKeys
        : mod.monthKey ? [mod.monthKey] : [];
      if (activeKeys.length === 0 || !activeKeys.includes(key)) continue;

      if (mod.startDate) {
        const sd = parseDate(mod.startDate);
        if (sd) {
          const modStartKey = mkKey(sd.getFullYear(), sd.getMonth() + 1);
          if (key < modStartKey) continue;
        }
      }

      const scope = mod.scope || "all";
      const appliesToMkt =
        scope === "all" ||
        (scope === "marketplace" && mod.marketplaceId === mkt.id) ||
        (scope === "product" && mod.marketplaceId === mkt.id);

      if (!appliesToMkt) continue;

      const pct = mod.value || 0;

      if (mod.type === "payout") {
        products = products.map(p => {
          const appliesToProd =
            scope === "all" ||
            scope === "marketplace" ||
            (scope === "product" && mod.productId === p.id);
          if (!appliesToProd) return p;
          return { ...p, payout: p.payout * (1 + pct) };
        });
      }

      if (mod.type === "units") {
        mktPctOfTotal = mktPctOfTotal * (1 + pct);
      }
    }

    return { ...mkt, pctOfTotal: mktPctOfTotal, products };
  });
}

// ─── Apply fulfilment/partner modifiers to expense amounts ───────────────────
export function applyExpenseModifiers(amount, itemId, itemType, modifiers, key) {
  if (!modifiers || modifiers.length === 0) return amount;
  let result = amount;
  for (const mod of sortedModifiers(modifiers)) {
    if (mod.type !== "fulfilment" && mod.type !== "partner") continue;
    const activeKeys = Array.isArray(mod.monthKeys) && mod.monthKeys.length > 0
      ? mod.monthKeys : mod.monthKey ? [mod.monthKey] : [];
    if (!activeKeys.includes(key)) continue;
    if (mod.startDate) {
      const sd = parseDate(mod.startDate);
      if (sd && key < mkKey(sd.getFullYear(), sd.getMonth() + 1)) continue;
    }
    if (mod.type === "fulfilment" && itemType === "fulfilment") {
      const targets = mod.fulfilmentIds || [];
      if (targets.length === 0 || targets.includes(itemId)) {
        result = result * (1 + (mod.value || 0));
      }
    }
    if (mod.type === "partner" && itemType === "partner") {
      const targets = mod.partnerIds || [];
      if (targets.length === 0 || targets.includes(itemId)) {
        result = result * (1 + (mod.value || 0));
      }
    }
  }
  return result;
}

// ─── Generate ad spend expense entries ─────────────────────────────────────
export function generateAdSpendExpenses(marketplaces, months, visibleYears) {
  const result = {};
  if (!Array.isArray(marketplaces) || !months) return result;

  for (const year of (visibleYears || [])) {
    for (let m = 1; m <= 12; m++) {
      const key = mkKey(year, m);
      const mData = months[key] || {};
      const target = mData.target || 0;
      if (target <= 0) continue;
      const mv = mData.vars || {};
      const dim = getDaysInMonth(year, m);

      for (const mkt of marketplaces) {
        if (!isMktActiveForKey(mkt, key)) continue;
        const mktPct = Number(mv[`pct_${mkt.id}`] ?? mkt.pctOfTotal) || 0;

        for (const p of (mkt.products || [])) {
          if (!p.adSpendPayments) continue;
          if (!isProdActiveForKey(p, key)) continue;

          const pPct = Number(mv[`pct_${mkt.id}_${p.id}`] ?? p.pctOfMarket) || 0;
          const prodUnits = Math.round(target * mktPct * pPct);
          if (prodUnits <= 0) continue;

          const adType = p.adCostType || (p.ppcType === "fixed" ? "fixed" : "pct");
          const payout = Number(mv[`payout_${mkt.id}_${p.id}`] ?? p.payout) || 0;
          let adCostPerUnit = 0;
          if (adType === "pct") {
            let rate = Number(p.adCostRate ?? p.ppcRate) || 0;
            if (rate > 1) rate = 1;
            adCostPerUnit = payout * rate;
          } else {
            adCostPerUnit = Number(p.adCostAmount) || 0;
          }
          if (adCostPerUnit <= 0) continue;

          const scheduleType = p.adSpendScheduleType || "monthly_day";
          const scheduleDay = p.adSpendScheduleDay || 1;

          if (scheduleType === "monthly_day") {
            const day = Math.min(scheduleDay, dim);
            const totalAd = Math.round(prodUnits * adCostPerUnit * 100) / 100;
            if (totalAd <= 0) continue;
            if (!result[key]) result[key] = [];
            result[key].push({
              id: `adspend_${mkt.id}_${p.id}_${key}`,
              amount: totalAd,
              what: `📢 Ad Spend — ${p.name || "Product"} (${mkt.name})`,
              date: toDateStr(year, m, day),
              isIncome: false,
              recurringId: `adspend_${mkt.id}_${p.id}`,
            });
          } else if (scheduleType === "every_x_days") {
            const interval = Math.max(1, scheduleDay);
            const dailyUnits = prodUnits / dim;
            for (let d = interval; d <= dim; d += interval) {
              const periodUnits = Math.round(dailyUnits * interval);
              const totalAd = Math.round(periodUnits * adCostPerUnit * 100) / 100;
              if (totalAd <= 0) continue;
              if (!result[key]) result[key] = [];
              result[key].push({
                id: `adspend_${mkt.id}_${p.id}_${key}_d${d}`,
                amount: totalAd,
                what: `📢 Ad Spend — ${p.name || "Product"} (${Math.round(interval)}-day cycle)`,
                date: toDateStr(year, m, Math.min(d, dim)),
                isIncome: false,
                recurringId: `adspend_${mkt.id}_${p.id}`,
              });
            }
          }
        }
      }
    }
  }
  return result;
}

// ─── Variable cost per unit (delivery + COGS) ──────────────────────────────
export function getVariableCostPerUnit(marketplaces, fbmSettings, inventorySummary) {
  let weightedDeliveryCost = 0;
  for (const mkt of (marketplaces || [])) {
    const mktPct = Number(mkt.pctOfTotal) || 0;
    for (const p of (mkt.products || [])) {
      const pPct = Number(p.pctOfMarket) || 0;
      const delCost = getEffectiveDeliveryCost(p, mkt, fbmSettings || []);
      weightedDeliveryCost += mktPct * pPct * delCost;
    }
  }
  const cogsPerUnit = inventorySummary?.weightedCostPerUnit || 0;
  return weightedDeliveryCost + cogsPerUnit;
}
